import { isDate } from 'date-fns';

export function tryGet<T, TVal>(obj: T | undefined, selector: (obj: T) => TVal) {
  if (obj != null) {
    return selector(obj);
  }
  return undefined;
}

export function getDateOrUndefined(
  maybeDate: string | undefined | null | number | Date,
): Date | undefined {
  if (maybeDate == null) {
    return undefined;
  }
  if (isDate(maybeDate) || maybeDate instanceof Date) {
    return new Date(maybeDate);
  }
  if (typeof maybeDate === 'string') {
    const timestamp = Date.parse(maybeDate);
    if (!isNaN(timestamp)) {
      return new Date(timestamp);
    }
  } else if (!isNaN(maybeDate)) {
    return new Date(maybeDate);
  }
  return undefined;
}

export function getNumberOrElse(
  value: string | number | null | undefined,
  defaultValue: number,
): number {
  const parsedValue = value == null || value === '' ? defaultValue : Number(value);
  return isNaN(parsedValue) ? defaultValue : parsedValue;
}

export function getOrElse<T, TDefault>(
  v: T | null | undefined,
  defaultValue: TDefault,
): NonNullable<T> | TDefault {
  return v != null ? (v as NonNullable<T>) : defaultValue;
}

export function getOrNull<T>(value: T): NonNullable<T> | null {
  return getOrElse(value, null);
}

export function getNumberOrNull<T>(value: T): number | null {
  if (value == null) {
    return null;
  }
  const maybeNumber = Number(value);
  if (isNaN(maybeNumber)) {
    return null;
  }
  return maybeNumber;
}

export function getOrUndefined<T>(value: T): NonNullable<T> | undefined {
  return getOrElse(value, undefined);
}

export function assertNotNull<T>(value: T | undefined, name: string): value is T;
export function assertNotNull<T>(value: T | null, name: string): value is T {
  if (value != null) {
    return true;
  }
  throw new Error(`Expected value of ${name} to be non-null`);
}

export function getOrThrow<T>(value: T | undefined, name: string): T;
export function getOrThrow<T>(value: T | null, name: string): T {
  if (value != null) {
    return value;
  }
  throw new Error(`Expected value of ${name} to be non-null`);
}

export function tryOrDefault<T>(action: () => T, onError: (err: Error) => void): T | undefined {
  try {
    return action();
  } catch (err: any) {
    onError(err);
  }
  return undefined;
}

export function getFromOrThrow<TIn, TOut>(obj: TIn, getter: (obj: TIn) => TOut, name?: string) {
  const result = getter(obj);
  if (result == null) {
    const message = `Expected non-null value`;
    throw new Error(name != null ? `${message}: ${name}` : message);
  }
  return result;
}

/** If property doesn't exist on object, returns default value */
export function propValueOrDefault<TIn, TOut>(
  obj: any,
  propName: keyof TIn,
  defaultValue: TOut,
): TOut {
  return propName in obj ? (obj[propName] as TOut) : defaultValue;
}

export function isEmail(email: string | null | undefined): boolean {
  if (email == null) {
    return false;
  }
  const re =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

export function getIntOrUndefined(value: any): number | undefined {
  try {
    const parsed = parseInt(value, 10);
    if (!isNaN(parsed)) {
      return parsed;
    }
  } catch (err) {}
  return undefined;
}

export function parseBoolean(value: string | boolean | 1 | 0 | undefined): boolean {
  return value === 'true' || value === true || value === 1 ? true : false;
}
